import React from 'react';
import {DirectionalHint, Icon, Text, TooltipHost, useTheme} from '@fluentui/react';
import {IProductImage} from '../../models/Search';
import {openEntity} from '../GlobalSearch/useGlobalSearchHotkey';

interface IProductThumbnailProps {
    src?: string;
    alt?: string;
    /** When set, clicking the thumbnail opens this product's details on its images tab. */
    productId?: string;
    /** Every image the product has; the flyout shows them all, with the thumbnail's own first. */
    images?: IProductImage[];
}

const MAX_PEEK_IMAGES = 6;

/**
 * A grid thumbnail with an image peek: when the product has more than one image, hovering shows
 * them at a useful size, the current one first, up to six. A single image gets no flyout.
 */
const ProductThumbnail: React.FC<IProductThumbnailProps> = ({src, alt, images, productId}) => {
    const theme = useTheme();
    const open = productId
        ? (e: React.MouseEvent) => { e.stopPropagation(); openEntity({kind: 'product', productId, initialTab: 'media'}); }
        : undefined;
    if (!src) {
        // No image yet: the slot still opens the product's images tab (where one can be added).
        if (!open) return null;
        return (
            <div className='grid-thumbnail-empty' onClick={open} title='Open the product images'>
                <Icon iconName='Photo2' />
            </div>
        );
    }
    const imgStyle: React.CSSProperties | undefined = open ? {cursor: 'pointer'} : undefined;
    const title = open ? 'Open the product images' : undefined;

    const distinct = [src, ...(images ?? []).map((i) => i.url).filter((u) => !!u)]
        .filter((u, i, all) => all.indexOf(u) === i);
    const urls = distinct.slice(0, MAX_PEEK_IMAGES);
    const more = distinct.length - urls.length;
    if (distinct.length < 2) return <img src={src} className='grid-thumbnail' alt={alt} onClick={open} style={imgStyle} title={title} />;

    const peek = (
        <div style={{maxWidth: 3 * 132 + 8}}>
            {alt && <Text variant='small' block styles={{root: {fontWeight: 600, marginBottom: 6}}}>{alt}</Text>}
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 8}}>
                {urls.map((u, i) => (
                    <img
                        key={u}
                        src={u}
                        alt={alt ? `${alt} image ${i + 1}` : ''}
                        style={{
                            width: 124,
                            height: 124,
                            objectFit: 'contain',
                            background: theme.palette.white,
                            border: `1px solid ${theme.palette.neutralLight}`,
                            borderRadius: 4,
                        }}
                    />
                ))}
            </div>
            {more > 0 && <Text variant='small' block styles={{root: {color: theme.palette.neutralSecondary, marginTop: 6}}}>+{more} more</Text>}
        </div>
    );

    return (
        <TooltipHost content={peek} directionalHint={DirectionalHint.rightCenter} calloutProps={{gapSpace: 6}}>
            <img src={src} className='grid-thumbnail' alt={alt} onClick={open} style={imgStyle} title={title} />
        </TooltipHost>
    );
};

export default ProductThumbnail;
